var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import getMessage from 'src/background/utils/i18n.utils';
import MkModule from '@background/mk.module';
import BgdAccountsUtils from '@background/utils/accounts.utils';
import { waitUntilDialogIsReady } from '@background/utils/window.utils';
import { MultisigStep, SocketMessageCommand, } from '@interfaces/multisig.interface';
import { KeysUtils } from '@popup/steem/utils/keys.utils';
import MkUtils from '@popup/steem/utils/mk.utils';
import { MultisigUtils } from '@popup/steem/utils/multisig.utils';
import { SteemTxUtils } from '@popup/steem/utils/steem-tx.utils';
import { BackgroundCommand } from '@reference-data/background-message-key.enum';
import { DialogCommand, MultisigDialogCommand, } from '@reference-data/dialog-message-key.enum';
import { LocalStorageKeyEnum } from '@reference-data/local-storage-key.enum';
import { sleep } from '@steempro/dsteem/lib/utils';
import { KeychainKeyTypes, } from '@steempro/steem-keychain-commons';
import { io } from 'socket.io-client';
import Config from 'src/config';
import LocalStorageUtils from 'src/utils/localStorage.utils';
import Logger from 'src/utils/logger.utils';
const signature = require('@hiveio/hive-js/lib/auth/ecc');
let socket;
let shouldReconnectSocket = false;
let connectedPublicKeys = [];
const lockedRequests = [];
const start = () => __awaiter(void 0, void 0, void 0, function* () {
    Logger.info(`Starting multisig`);
    socket = io(Config.multisig.baseURL, {
        transports: ['websocket'],
        reconnection: true,
        autoConnect: false,
    });
    const multisigConfig = yield LocalStorageUtils.getValueFromLocalStorage(LocalStorageKeyEnum.MULTISIG_CONFIG);
    if (multisigConfig &&
        Object.values(multisigConfig).some((config) => config.isEnabled &&
            (config.active.isEnabled || config.posting.isEnabled))) {
        Logger.info('Some accounts need connection');
        shouldReconnectSocket = true;
        if (!socket.connected)
            socket.connect();
        connectSocket(multisigConfig);
    }
    else {
        Logger.info('Multisig hasnt been enabled for any account');
    }
    setupPopupListener();
    setupRefreshConnections();
});
const setupRefreshConnections = () => {
    chrome.runtime.onMessage.addListener((backgroundMessage, sender, sendResp) => __awaiter(void 0, void 0, void 0, function* () {
        if (backgroundMessage.command ===
            BackgroundCommand.MULTISIG_REFRESH_CONNECTIONS) {
            const value = backgroundMessage.value;
            const multisigConfig = yield LocalStorageUtils.getValueFromLocalStorage(LocalStorageKeyEnum.MULTISIG_CONFIG);
            const accountMultisigConfig = multisigConfig[value.account];
            if (value.connect) {
                if (!socket.connected)
                    socket.connect();
                yield sleep(1000);
                connectSocket(multisigConfig);
                shouldReconnectSocket = true;
                connectToBackend(value.account, accountMultisigConfig);
            }
            else {
                if (value.publicKey && value.publicKey.length > 0) {
                    disconnectFromBackend(value.account, value.publicKey);
                }
                else {
                    disconnectFromBackend(value.account, accountMultisigConfig.active.publicKey);
                    disconnectFromBackend(value.account, accountMultisigConfig.posting.publicKey);
                }
            }
        }
    }));
};
const setupPopupListener = () => {
    chrome.runtime.onMessage.addListener((backgroundMessage, sender, sendResp) => __awaiter(void 0, void 0, void 0, function* () {
        if (backgroundMessage.command ===
            BackgroundCommand.MULTISIG_REQUEST_SIGNATURES) {
            const data = backgroundMessage.value;
            yield createConnectionIfNeeded(data);
            requestSignatures(data, true);
        }
    }));
};
// When the socket has not been initialized because multisig is not enabled for any account
// this allows to create a connection on the go to wait for a multisig response
const createConnectionIfNeeded = (data) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    if (!socket.connected) {
        shouldReconnectSocket = true;
        socket.connect();
        connectSocket({});
        yield sleep(1000);
    }
    const config = (yield LocalStorageUtils.getValueFromLocalStorage(LocalStorageKeyEnum.MULTISIG_CONFIG)) || {};
    if (!((_a = config[data.initiatorAccount.name]) === null || _a === void 0 ? void 0 : _a[(_b = data.method) === null || _b === void 0 ? void 0 : _b.toLowerCase()].isEnabled)) {
        const config = {
            isEnabled: true,
            posting: data.method.toLowerCase() === 'posting'
                ? {
                    isEnabled: true,
                    publicKey: KeysUtils.getPublicKeyFromPrivateKeyString(data.key),
                    message: signMessage(data.initiatorAccount.name, (_c = data.key) === null || _c === void 0 ? void 0 : _c.toString()),
                }
                : { isEnabled: false, message: '', publicKey: '' },
            active: data.method.toLowerCase() === 'active'
                ? {
                    isEnabled: true,
                    publicKey: KeysUtils.getPublicKeyFromPrivateKeyString(data.key),
                    message: signMessage(data.initiatorAccount.name, (_d = data.key) === null || _d === void 0 ? void 0 : _d.toString()),
                }
                : { isEnabled: false, message: '', publicKey: '' },
        };
        yield connectToBackend(data.initiatorAccount.name, config);
        yield sleep(1000);
    }
});
const requestSignatures = (data, useRuntimeMessages) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        yield createConnectionIfNeeded(data);
        const message = yield getRequestSignatureMessage(data);
        try {
            socket.volatile.emit(SocketMessageCommand.REQUEST_SIGNATURE, message, withTimeout((message) => __awaiter(void 0, void 0, void 0, function* () {
                Logger.log({ multisigRequestSignatureResponse: message });
                if (useRuntimeMessages) {
                    chrome.runtime.sendMessage({
                        command: BackgroundCommand.MULTISIG_REQUEST_SIGNATURES_RESPONSE,
                        value: {
                            message: 'multisig_transaction_sent_to_signers',
                        },
                    });
                }
                else {
                    // resolve('multisig_transaction_sent_to_signers');
                    // in this case try to wait for broadcast notification
                    try {
                        const { txId, id } = (yield waitForBroadcastToBeDone());
                        if (!lockedRequests.includes(id)) {
                            lockedRequests.push(id);
                            resolve(txId);
                        }
                    }
                    catch (err) {
                        chrome.runtime.sendMessage({
                            command: DialogCommand.SEND_DIALOG_ERROR,
                            msg: { display_msg: yield getMessage(err) },
                        });
                        resolve({ error: { message: err } });
                    }
                }
            }), () => {
                Logger.info('timeout in socketio');
            }));
        }
        catch (err) {
            Logger.error({ err });
        }
    }));
});
const initAccountsConnections = (multisigConfig) => __awaiter(void 0, void 0, void 0, function* () {
    for (const accountName of Object.keys(multisigConfig)) {
        const multisigAccountConfig = multisigConfig[accountName];
        if (multisigAccountConfig && multisigAccountConfig.isEnabled) {
            connectToBackend(accountName, multisigAccountConfig);
        }
    }
});
const connectSocket = (multisigConfig) => {
    socket.removeAllListeners(SocketMessageCommand.REQUEST_SIGN_TRANSACTION);
    socket.removeAllListeners(SocketMessageCommand.TRANSACTION_BROADCASTED_NOTIFICATION);
    socket.removeAllListeners(SocketMessageCommand.TRANSACTION_ERROR_NOTIFICATION);
    socket.removeAllListeners('connect');
    socket.removeAllListeners('error');
    socket.removeAllListeners('disconnect');
    socket.on('connect', () => {
        Logger.info('Connected to socket');
        keepAlive();
        initAccountsConnections(multisigConfig);
    });
    socket.on('error', (err) => {
        Logger.error('Error in socket', err);
    });
    socket.on('disconnect', (ev) => {
        Logger.info('Disconnected from socket');
        socket.connect();
    });
    socket.on(SocketMessageCommand.REQUEST_SIGN_TRANSACTION, (signatureRequest) => __awaiter(void 0, void 0, void 0, function* () {
        const signerIndex = signatureRequest.signers.findIndex((signer) => {
            return signer.publicKey === signatureRequest.targetedPublicKey;
        });
        if (signerIndex === -1) {
            return;
        }
        const signer = signatureRequest.signers[signerIndex];
        yield sleep(800 * (signerIndex + 2));
        const signedTransaction = yield MultisigModule.processSignatureRequest(signatureRequest, signer);
        chrome.runtime.sendMessage({
            command: MultisigDialogCommand.MULTISIG_SEND_DATA_TO_POPUP,
            value: {
                multisigStep: MultisigStep.SIGN_TRANSACTION_FEEDBACK,
                data: {
                    message: 'multisig_dialog_transaction_signed_successfully',
                    success: true,
                    signer: signer,
                },
            },
        });
        if (signedTransaction) {
            socket.emit(SocketMessageCommand.SIGN_TRANSACTION, {
                signature: signedTransaction.signatures[0],
                signerId: signer.id,
                signatureRequestId: signatureRequest.id,
            }, (signatures) => __awaiter(void 0, void 0, void 0, function* () {
                Logger.info(`Should try to broadcast ${JSON.stringify(signedTransaction)}`);
                const txResult = yield SteemTxUtils.broadcastAndConfirmTransactionWithSignature({
                    expiration: signedTransaction.expiration,
                    extensions: signedTransaction.extensions,
                    operations: signedTransaction.operations,
                    ref_block_num: signedTransaction.ref_block_num,
                    ref_block_prefix: signedTransaction.ref_block_prefix,
                }, signatures, true);
                if (txResult === null || txResult === void 0 ? void 0 : txResult.confirmed) {
                    socket.emit(SocketMessageCommand.NOTIFY_TRANSACTION_BROADCASTED, {
                        signatureRequestId: signatureRequest.id,
                        txId: txResult.tx_id,
                    }, () => {
                        Logger.info(`Notified`);
                    });
                }
            }));
        }
        else {
            //TODO  check if need to return if no rejected
        }
    }));
    socket.on(SocketMessageCommand.TRANSACTION_BROADCASTED_NOTIFICATION, (signatureRequest, txId) => __awaiter(void 0, void 0, void 0, function* () {
        Logger.log(`signature request ${signatureRequest.id} was broadcasted`, txId);
        const transaction = yield SteemTxUtils.getTransaction(txId);
        delete transaction.signatures;
        if (!lockedRequests.includes(signatureRequest.id)) {
            lockedRequests.push(signatureRequest.id);
            openWindow({
                multisigStep: MultisigStep.NOTIFY_TRANSACTION_BROADCASTED,
                data: {
                    message: 'multisig_dialog_transaction_broadcasted',
                    success: true,
                    txId: txId,
                    transaction: transaction,
                },
            });
        }
    }));
    socket.on(SocketMessageCommand.TRANSACTION_ERROR_NOTIFICATION, (e) => __awaiter(void 0, void 0, void 0, function* () {
        yield sleep(200);
        if (!lockedRequests.includes(e.signatureRequest.id)) {
            lockedRequests.push(e.signatureRequest.id);
            openWindow({
                multisigStep: MultisigStep.NOTIFY_ERROR,
                data: {
                    message: e.error.message,
                    success: false,
                },
            });
        }
    }));
    if (socket) {
        socket.connect();
    }
};
const disconnectFromBackend = (accountName, publicKey) => __awaiter(void 0, void 0, void 0, function* () {
    Logger.info(`Trying to disconnect @${accountName} (${publicKey}) from backend`);
    connectedPublicKeys = connectedPublicKeys.filter((pk) => pk.username === accountName && pk.publicKey === publicKey);
    socket.emit(SocketMessageCommand.SIGNER_DISCONNECT, publicKey);
});
const connectToBackend = (accountName, accountConfig) => __awaiter(void 0, void 0, void 0, function* () {
    var _e, _f, _g, _h, _j, _k;
    Logger.info(`Connecting @${accountName} to the multisig backend server`);
    const signerConnectMessages = [];
    if (((_e = accountConfig.active) === null || _e === void 0 ? void 0 : _e.isEnabled) &&
        !connectedPublicKeys
            .map((cpk) => cpk.publicKey)
            .includes((_g = (_f = accountConfig.active) === null || _f === void 0 ? void 0 : _f.publicKey) === null || _g === void 0 ? void 0 : _g.toString())) {
        signerConnectMessages.push({
            username: accountName,
            publicKey: accountConfig.active.publicKey,
            message: accountConfig.active.message,
        });
    }
    if (((_h = accountConfig.posting) === null || _h === void 0 ? void 0 : _h.isEnabled) &&
        !connectedPublicKeys
            .map((cpk) => cpk.publicKey)
            .includes((_k = (_j = accountConfig.posting) === null || _j === void 0 ? void 0 : _j.publicKey) === null || _k === void 0 ? void 0 : _k.toString())) {
        signerConnectMessages.push({
            username: accountName,
            publicKey: accountConfig.posting.publicKey,
            message: accountConfig.posting.message,
        });
    }
    socket.emit(SocketMessageCommand.SIGNER_CONNECT, signerConnectMessages, (signerConnectResponse) => {
        for (const signer of signerConnectMessages) {
            if (!(signerConnectResponse.errors &&
                Object.keys(signerConnectResponse.errors).includes(signer.publicKey))) {
                connectedPublicKeys.push(signer);
            }
        }
    });
});
const keepAlive = () => {
    const keepAliveIntervalId = setInterval(() => {
        if (socket) {
            socket.emit('ping');
        }
        else {
            clearInterval(keepAliveIntervalId);
        }
    }, 10 * 1000);
};
const getRequestSignatureMessage = (data) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        var _l, _m, _o, _p, _q, _r;
        const potentialSigners = yield MultisigUtils.getPotentialSigners(data.transactionAccount, data.key, data.method);
        const signers = [];
        for (const [receiverPubKey, weight] of potentialSigners) {
            const metaData = (_m = (_l = data.options) === null || _l === void 0 ? void 0 : _l.metaData) !== null && _m !== void 0 ? _m : {};
            const usernames = yield KeysUtils.getKeyReferences([receiverPubKey]);
            let twoFACodes = {};
            if ((_p = (_o = data.options) === null || _o === void 0 ? void 0 : _o.metaData) === null || _p === void 0 ? void 0 : _p.twoFACodes) {
                twoFACodes = {
                    [usernames[0]]: yield encodeMetadata((_r = (_q = data.options) === null || _q === void 0 ? void 0 : _q.metaData) === null || _r === void 0 ? void 0 : _r.twoFACodes[usernames[0]], data.key.toString(), receiverPubKey),
                };
            }
            signers.push({
                encryptedTransaction: yield encodeTransaction(data.transaction, data.key.toString(), receiverPubKey),
                publicKey: receiverPubKey,
                weight: weight.toString(),
                metaData: Object.assign(Object.assign({}, metaData), { twoFACodes: twoFACodes }),
            });
        }
        const publicKey = KeysUtils.getPublicKeyFromPrivateKeyString(data.key.toString());
        const keyAuths = data.method === KeychainKeyTypes.active
            ? data.initiatorAccount.active.key_auths
            : data.initiatorAccount.posting.key_auths;
        const keyAuth = keyAuths.find(([key, weight]) => key.toString() === publicKey.toString());
        const transactionAccountThreshold = data.method === KeychainKeyTypes.active
            ? data.initiatorAccount.active.weight_threshold
            : data.initiatorAccount.posting.weight_threshold;
        const request = {
            initialSigner: {
                publicKey: publicKey,
                signature: data.signature,
                username: data.initiatorAccount.name,
                weight: keyAuth[1],
            },
            signatureRequest: {
                expirationDate: data.transaction.expiration,
                keyType: data.method,
                signers: signers,
                threshold: transactionAccountThreshold,
            },
        };
        resolve(request);
    }));
});
const processSignatureRequest = (signatureRequest, signer) => __awaiter(void 0, void 0, void 0, function* () {
    var _s, _t;
    if (signer) {
        const username = (_s = connectedPublicKeys.find((c) => c.publicKey === signer.publicKey)) === null || _s === void 0 ? void 0 : _s.username;
        let mk = yield MkModule.getMk();
        let openNewWindow = true;
        if (!mk) {
            mk = yield unlockWallet(signer);
            openNewWindow = false;
        }
        const localAccounts = yield BgdAccountsUtils.getAccountsFromLocalStorage(mk);
        const localAccount = localAccounts.find((la) => la.name === username);
        const key = (_t = localAccount === null || localAccount === void 0 ? void 0 : localAccount.keys[signatureRequest.keyType.toLowerCase()]) === null || _t === void 0 ? void 0 : _t.toString();
        const decodedTransaction = yield decryptRequest(signer, key);
        if (decodedTransaction) {
            const signedTransaction = yield requestSignTransactionFromUser(decodedTransaction, signer, signatureRequest, key, openNewWindow);
            return signedTransaction;
        }
        else {
            return;
        }
    }
});
const unlockWallet = (signer) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
        const onReceiveMK = (backgroundMessage, sender, sendResp) => __awaiter(void 0, void 0, void 0, function* () {
            if (backgroundMessage.command === BackgroundCommand.MULTISIG_UNLOCK_WALLET) {
                if (backgroundMessage.value) {
                    try {
                        if (yield MkUtils.login(backgroundMessage.value)) {
                            resolve(backgroundMessage.value);
                            chrome.runtime.onMessage.removeListener(onReceiveMK);
                        }
                        else {
                            chrome.runtime.sendMessage({
                                command: MultisigDialogCommand.MULTISIG_SEND_DATA_TO_POPUP,
                                value: {
                                    multisigStep: MultisigStep.UNLOCK_WALLET,
                                    data: { feedback: 'wrong_password' },
                                },
                            });
                        }
                    }
                    catch (err) {
                        chrome.runtime.sendMessage({
                            command: MultisigDialogCommand.MULTISIG_SEND_DATA_TO_POPUP,
                            value: {
                                multisigStep: MultisigStep.UNLOCK_WALLET,
                                data: { feedback: 'wrong_password' },
                            },
                        });
                    }
                }
                else {
                    resolve(undefined);
                }
            }
        });
        chrome.runtime.onMessage.addListener(onReceiveMK);
        openWindow({
            multisigStep: MultisigStep.UNLOCK_WALLET,
            data: { signer: signer },
        });
    });
});
const requestSignTransactionFromUser = (decodedTransaction, signer, signatureRequest, key, openNewWindow) => {
    return new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        const onReceivedMultisigAcceptResponse = (backgroundMessage, sender, sendResp) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            if (backgroundMessage.command ===
                BackgroundCommand.MULTISIG_ACCEPT_RESPONSE &&
                ((_a = backgroundMessage.value) === null || _a === void 0 ? void 0 : _a.multisigData.data.signer.id) === signer.id) {
                if (backgroundMessage.value.accepted) {
                    const signedTransaction = yield SteemTxUtils.signTransaction(decodedTransaction, key);
                    resolve(signedTransaction);
                    chrome.runtime.onMessage.removeListener(onReceivedMultisigAcceptResponse);
                }
                else {
                    resolve(undefined);
                }
            }
        });
        chrome.runtime.onMessage.addListener(onReceivedMultisigAcceptResponse);
        const usernames = yield KeysUtils.getKeyReferences([signer.publicKey]);
        if (openNewWindow) {
            openWindow({
                multisigStep: MultisigStep.ACCEPT_REJECT_TRANSACTION,
                data: {
                    username: usernames[0],
                    signer,
                    signatureRequest,
                    decodedTransaction,
                },
            });
        }
        else {
            chrome.runtime.sendMessage({
                command: MultisigDialogCommand.MULTISIG_SEND_DATA_TO_POPUP,
                value: {
                    multisigStep: MultisigStep.ACCEPT_REJECT_TRANSACTION,
                    data: {
                        username: usernames[0],
                        signer,
                        signatureRequest,
                        decodedTransaction,
                    },
                },
            });
        }
    }));
};
const decryptRequest = (signer, key) => __awaiter(void 0, void 0, void 0, function* () {
    return yield MultisigUtils.decodeTransaction(signer.encryptedTransaction, key);
});
const encodeTransaction = (transaction, key, receiverPublicKey) => __awaiter(void 0, void 0, void 0, function* () {
    return yield MultisigUtils.encodeTransaction(transaction, key, receiverPublicKey);
});
const encodeMetadata = (metaData, key, receiverPublicKey) => __awaiter(void 0, void 0, void 0, function* () {
    return yield MultisigUtils.encodeMetadata(metaData, key, receiverPublicKey);
});
const openWindow = (data) => {
    chrome.windows.getCurrent((currentWindow) => __awaiter(void 0, void 0, void 0, function* () {
        const win = {
            url: chrome.runtime.getURL('multisig-dialog.html'),
            type: 'popup',
            height: 600,
            //      width: 435,
            width: 400,
            left: currentWindow.width - 350 + currentWindow.left,
            top: currentWindow.top,
            focused: true,
        };
        // Except on Firefox
        //@ts-ignore
        if (typeof InstallTrigger === undefined)
            win.focused = true;
        chrome.windows.create(win, (window) => {
            waitUntilDialogIsReady(100, MultisigDialogCommand.READY_MULTISIG, () => {
                chrome.runtime.sendMessage({
                    command: MultisigDialogCommand.MULTISIG_SEND_DATA_TO_POPUP,
                    value: data,
                });
            });
        });
    }));
};
const withTimeout = (onSuccess, onTimeout, timeout = 5000) => {
    let called = false;
    const timer = setTimeout(() => {
        if (called)
            return;
        called = true;
        onTimeout();
    }, timeout);
    return (...args) => {
        if (called)
            return;
        called = true;
        clearTimeout(timer);
        onSuccess.apply(this, args);
    };
};
const waitForBroadcastToBeDone = () => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
        const broadcastedListener = (signatureRequest, txId) => __awaiter(void 0, void 0, void 0, function* () {
            socket.off(SocketMessageCommand.TRANSACTION_ERROR_NOTIFICATION, notifyError);
            socket.off(SocketMessageCommand.TRANSACTION_BROADCASTED_NOTIFICATION, broadcastedListener);
            resolve({ txId, id: signatureRequest.id });
        });
        const notifyError = (res) => __awaiter(void 0, void 0, void 0, function* () {
            socket.off(SocketMessageCommand.TRANSACTION_ERROR_NOTIFICATION, notifyError);
            socket.off(SocketMessageCommand.TRANSACTION_BROADCASTED_NOTIFICATION, broadcastedListener);
            if (!lockedRequests.includes(res.signatureRequest.id)) {
                lockedRequests.push(res.signatureRequest.id);
                reject(res.error.message);
            }
        });
        socket.on(SocketMessageCommand.TRANSACTION_BROADCASTED_NOTIFICATION, broadcastedListener);
        socket.on(SocketMessageCommand.TRANSACTION_ERROR_NOTIFICATION, notifyError);
    });
});
setInterval(() => {
    if (shouldReconnectSocket && (!socket || !socket.connected)) {
        Logger.log('Restarting the socket');
        start();
    }
}, 60 * 1000);
const signMessage = (message, privateKey) => {
    let buf;
    try {
        const o = JSON.parse(message, (k, v) => {
            if (v !== null &&
                typeof v === 'object' &&
                'type' in v &&
                v.type === 'Buffer' &&
                'data' in v &&
                Array.isArray(v.data)) {
                return Buffer.from(v.data);
            }
            return v;
        });
        if (Buffer.isBuffer(o)) {
            buf = o;
        }
        else {
            buf = message;
        }
    }
    catch (e) {
        buf = message;
    }
    return signature.Signature.signBuffer(buf, privateKey).toHex();
};
export const MultisigModule = {
    start,
    processSignatureRequest,
    requestSignatures,
    encodeMetadata,
};
